<script lang="ts">
    import { tikTokGalleryWidgetContextToken } from "@apps/tiktok/widgets/tiktok_gallery_widget/context_tokens";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import type ProductVariantsEntity from "@modules/products/entities/ProductVariantsEntity";
    import SelectProductVariantManager from "@modules/products/utils/SelectProductVariantManager";
    import CN from "classnames";
    import { of } from "rxjs";
    import {
        filter,
        map,
        mergeMap,
        startWith,
        switchMap,
    } from "rxjs/operators";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import SelectOptionField from "@components/forms/SelectOptionField/SelectOptionField.svelte";
    import ManagedModalWithDialog from "@components/Modal/ManagedModalWithDialog.svelte";
    import { ModalToken } from "@components/Modal/ModalManager";
    import { modalManagerContextKey } from "@components/Modal/ModalManager/constants";
    import PostModalProductImage from "@components/PostModal/PostModalProductImage.svelte";
    import PostModalProductInfo from "@components/PostModal/PostModalProductInfo.svelte";
    import PostModalProductView from "@components/PostModal/PostModalProductView.svelte";
    import Tabs from "@components/Tabs/Tabs.svelte";
    import TabsManager from "@components/Tabs/TabsManager";
    import TabsMenu from "@components/Tabs/TabsMenu.svelte";
    import TabsPanel from "@components/Tabs/TabsPanel.svelte";

    import TikTokPostModalManager from "./TikTokPostModalManager";
    import TikTokPostModalPostView from "./TikTokPostModalPostView.svelte";

    export let modalToken: ModalToken<{ id: number }>;
    export let hideBranding: boolean;
    export let productQuickViewEnabled: boolean;
    export let tikTokPostModalManager: TikTokPostModalManager;
    export let urlsBuilder: URLsBuilder;

    const {
        taggedProductQuery,
        cartService,
        cartQuery,
        locationController,
        productsQuery,
        cartAvailabilityProvider,
    } = getTypedContext(tikTokGalleryWidgetContextToken);

    const isCartAvailable = cartAvailabilityProvider.isCartAvailable();

    const i18nModule = container.resolve(I18nModule);
    const descriptionT$ = i18nModule.exports.i18n.selectT(
        "tiktok.critical.TikTokGalleryWidget",
        "description"
    );

    const tabsManager = new TabsManager();
    const openedTaggedProduct$ = taggedProductQuery.product$;

    const openedProductTag$ = tikTokPostModalManager.openedProductTag$;
    const product$ = openedProductTag$.pipe(
        switchMap((openedProductTag) =>
            openedProductTag
                ? productsQuery.selectProduct(openedProductTag.product.id)
                : of(null)
        )
    );
    const postTags$ = tikTokPostModalManager.activePost$.pipe(
        map((post) => post?.tags)
    );
    const selectProductVariantManager = new SelectProductVariantManager(
        product$,
        openedProductTag$.pipe(
            map(
                (openedProductTag) =>
                    openedProductTag?.product.variant.id || null
            )
        )
    );
    const selectedVariant$ =
        selectProductVariantManager.selectedProductVariant$;
    const selectedVariantImageId$ = selectedVariant$.pipe(
        map((selectedVariant) => {
            return selectedVariant && selectedVariant.image
                ? selectedVariant.image.id
                : null;
        })
    );

    const tabs$ = descriptionT$.pipe(
        map((descriptionT) => [{ name: "description", label: descriptionT }]),
        startWith([])
    );
    const modalManager = getTypedContext(modalManagerContextKey);

    modalManager.addModal(modalToken);
    const modalParams$ = modalManager.selectParams(modalToken);
    const modalIsOpen$ = modalManager.selectIsOpen(modalToken);
    const cartStatus$ = selectedVariant$.pipe(
        filter((v): v is ProductVariantsEntity => !!v),
        switchMap((selectedVariant) => {
            return cartQuery.selectEditItemStatus(selectedVariant.id);
        })
    );
    const handleClose = () => {
        modalManager.closeModal(modalToken);
    };
    const handleChange = (value: string, index: number) =>
        selectProductVariantManager.changeOption({ value, index });

    const handleCheckout = () =>
        locationController.goTo(urlsBuilder.createCartUrl());

    const addToCard = async () => {
        if ($selectedVariant$) {
            await cartService.addToCart([
                {
                    variantID: $selectedVariant$.id,
                    quantity: 1,
                },
            ]);
        }
    };
    onMount(() => {
        cartService.getCart();
        const modalParamsSubscription = modalParams$.subscribe((params) => {
            if (!params) return;
            tikTokPostModalManager.changeActivePost(params.id);
        });
        const modalOpenSubscription = modalIsOpen$.subscribe((isOpen) => {
            if (!isOpen) tikTokPostModalManager.openProductTag(null);
        });
        const tabsSubscription = tabs$
            .pipe(mergeMap((v) => v))
            .subscribe((t) => {
                tabsManager.registerTab(t.name);
            });
        return () => {
            modalParamsSubscription.unsubscribe();
            modalOpenSubscription.unsubscribe();
            tabsSubscription.unsubscribe();
        };
    });
    $: rerenderKey = $product$?.id;
</script>

<ManagedModalWithDialog
    modalStyle="--gw-modal-padding: 0"
    {modalToken}
    modalClass="gw-tt-post-modal"
    dialogClass={CN("gw-tt-post-modal-dialog", {
        "gw-tt-post-modal-dialog--width-auto": !$openedProductTag$,
    })}
>
    {#if $modalIsOpen$}
        {#if $openedProductTag$}
            <PostModalProductView
                productViewController={{
                    nextProductTag() {
                        tikTokPostModalManager.nextProductTag();
                    },
                    prevProductTag() {
                        tikTokPostModalManager.prevProductTag();
                    },
                    close() {
                        tikTokPostModalManager.openProductTag(null);
                    },
                }}
                showControlls={$postTags$ ? $postTags$.length > 1 : false}
                on:close={handleClose}
            >
                <svelte:fragment slot="productImage">
                    {#key rerenderKey}
                        <PostModalProductImage
                            images={$product$ ? $product$.images : []}
                            imageId$={selectedVariantImageId$}
                            altText={$product$?.title}
                        />
                    {/key}
                </svelte:fragment>
                {#if $product$}
                    <PostModalProductInfo
                        slot="productInfo"
                        selectedVariant={$selectedVariant$
                            ? {
                                  title: $selectedVariant$.title,
                                  id: $selectedVariant$.id,
                                  compareAtPrice:
                                      $selectedVariant$.compareAtPrice
                                          ?.amount || null,
                                  price: $selectedVariant$.price.amount,
                                  available: $selectedVariant$.availableForSale,
                              }
                            : null}
                        productTitle={$openedProductTag$.product.title}
                        productUrl={urlsBuilder.createProductUrl(
                            $product$.handle
                        )}
                        cartStatus={$cartStatus$}
                        {isCartAvailable}
                        on:addToCard={addToCard}
                        on:handleCheckout={handleCheckout}
                    >
                        <svelte:fragment>
                            {#if $openedTaggedProduct$}
                                <div
                                    class="gw-post-modal-product-info__variants"
                                >
                                    {#each $product$.options.filter((option) => option.values.length > 1 || ($product$?.options?.length ?? 0) > 1) as option, idx (option.name)}
                                        <SelectOptionField
                                            optionsSet={{
                                                name: option.name,
                                                options: option.values.map(
                                                    (value) => ({
                                                        label: value,
                                                        value,
                                                    })
                                                ),
                                            }}
                                            name={`productOption-${option.name}`}
                                            value={selectProductVariantManager.getByIndex(
                                                idx
                                            )}
                                            on:change={(e) =>
                                                handleChange(e.detail, idx)}
                                        />
                                    {/each}
                                </div>
                            {/if}
                        </svelte:fragment>
                    </PostModalProductInfo>
                {/if}
                <Tabs
                    class="gw-post-modal-product-view__tabs"
                    tabsName="ig-post-modal-product-view-tab"
                >
                    {#if $tabs$}
                        <TabsMenu
                            {tabsManager}
                            tabs={$tabs$}
                            slot="menu"
                            position="start"
                        />
                    {/if}
                    <svelte:fragment slot="body">
                        <TabsPanel {tabsManager} name={"description"}>
                            <div
                                class="gw-post-modal-product-view__tabpanel-wrapper"
                            >
                                {@html $product$ ? $product$.description : ""}
                            </div>
                        </TabsPanel>
                    </svelte:fragment>
                </Tabs>
            </PostModalProductView>
        {:else}
            <TikTokPostModalPostView
                {tikTokPostModalManager}
                {urlsBuilder}
                {productQuickViewEnabled}
                {hideBranding}
                on:close={handleClose}
            />
        {/if}
    {/if}
</ManagedModalWithDialog>

<style>
    :global(.gw-modal-dialog.gw-tt-post-modal-dialog--width-auto) {
        width: auto !important;
        overflow: visible;
    }
    :global(.gw-tt-post-modal .gw-modal-header__close_btn) {
        visibility: hidden;
    }
    .gw-post-modal-product-view__tabpanel-wrapper {
        padding-top: 5px;
        color: var(--gw-dark-text-color);
        font-weight: 300;
    }
    .gw-post-modal-product-view__tabpanel-wrapper {
        padding: 10px 0;
    }

    .gw-post-modal-product-info__variants {
        display: flex;
        flex-wrap: wrap;
        padding: 16px 0;
        row-gap: 16px;
    }

    :global(.gw-post-modal-product-view__tabs) {
        --gw-tabs-menu-font-size: var(--gw-font-size-lg);
    }

    @media all and (min-width: 1200px) {
        :global(.gw-tt-post-modal .gw-modal-dialog.gw-tt-post-modal-dialog) {
            max-width: 70vw;
        }
    }
</style>
